<template>
  <teleport to="head">
    <MetaTags
      :title="`Reverse IP lookup for ${ip}`"
      :description="`This is the canonical hostname for ${ip}. It has been configured by the owner of this IP address.`"
      :url="`https://www.nslookup.io/ip/${domainPunycode}/domain-names/`"
    />
  </teleport>
  <HeaderComponent :domain-search-to-url="(encodedDomain: String) => `/ip/${encodedDomain}/domain-names/`" search-button-text="Reverse lookup" :domain="ip" :autocomplete-enabled="false" />
  <div class="flex-grow bg-neutral-100">
    <main class="container mx-auto max-w-6xl">
      <div class="flex flex-col justify-between mt-8 mb-6 ml-6 sm:flex-row sm:items-center">
        <h1 class="text-3xl">Reverse IP lookup for <b>{{ ip }}</b></h1>
        <dns-records-link :domain="ip" class="shrink-0 hidden mr-6 sm:block xl:mr-0">
          <button type="button" class="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">All DNS records</button>
        </dns-records-link>
      </div>
      <div class="bg-white shadow p-6 pb-10 rounded-t">
        <ApiErrorAlert v-if="error !== null" :error="error" :domain="ip" dnsserver="authoritative" />
        <div v-else-if="loading" aria-hidden="true">
          <SkeletonComponent class="w-56 h-8 mb-6" />
          <div v-for="n in 2" :key="n">
            <SkeletonComponent v-for="n in 2" :key="n" class="inline-block w-1/2 h-4 pr-[10%] bg-clip-content" />

          </div>
          <SkeletonComponent class="inline-block w-56 h-8 my-6" />
          <SkeletonComponent class="w-24 h-4 mb-2" />
          <SkeletonComponent class="w-1/2 h-4 mb-6" />
          <template v-for="n in 3" :key="n">
            <SkeletonComponent class="w-24 h-4 mb-2 block" />
            <SkeletonComponent class="w-1/2 h-4 mb-2 block" />
            <SkeletonComponent class="w-1/2 h-4 mb-6" />
          </template>
        </div>
        <template v-else>
          <p class="mb-3">Our DNS servers responded with these IP addresses when we queried it for the domain {{ ip }}. Some DNS servers may return different IP addresses based on your location.</p>
          <div v-if="queryResult.response.answer.length === 0" class="mt-4">
            <Alert type="warning">
              <span><span class="font-semibold">{{ ip }}</span> does not have a reverse lookup record.</span>
            </Alert>
          </div>
          <Alert v-else type="success" class="mt-4">
            The canonical hostname of <span class="font-semibold">{{ ip }}</span> is <dns-records-link :domain="queryResult.response.answer[0].record.target" class="font-semibold hover:underline" />
            <copy-component :content="queryResult.response.answer[0].record.target" />
          </Alert>
          <AdUnit class="mt-8 mx-auto" :config="ReverseIpResults" />
          <h2 class="mt-8 mb-2 font-semibold text-2xl">Question and response</h2>
          <raw-records :records="queryResult" />
        </template>
        <h2 class="mt-8 mb-2 font-semibold text-2xl">Find the domain name for another IP address</h2>
        <div class="flex flex-col lg:flex-row">
          <p class="shrink max-w-xl">With <router-link to="/reverse-ip-lookup/" class="text-blue-600 hover:text-blue-800 hover:underline">reverse IP lookup</router-link>, you can find the domain name for any IP address.
            When you enter an IPv4 or IPv6 address in the input field below, it will show the canonical domain name for that IP address.</p>
          <form class="mt-3 w-[500px] max-w-full sm:px-6 lg:mt-0" autocomplete="off">
            <DomainSearch placeholder-text="IP address" emphasize buttonText="Reverse lookup" :prefill="ip" @submitted="redirectTo" :autocomplete-enabled="false" />
          </form>
        </div>
      </div>
    </main>
  </div>
  <FooterComponent />
</template>

<script setup lang="ts">
import axios, { Canceler } from 'axios';
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import ApiErrorAlert from '@/components/ApiErrorAlert.vue';
import DomainSearch from '@/components/DomainSearch.vue';
import FooterComponent from '@/components/layout/FooterComponent.vue';
import HeaderComponent from '@/components/layout/HeaderComponent.vue';
import MetaTags from '@/components/MetaTags.vue';
import Alert from '@/components/micro/AlertComponent.vue';
import CopyComponent from '@/components/micro/CopyComponent.vue';
import DnsRecordsLink from '@/components/micro/DnsRecordsLink.vue';
import RawRecords from '@/components/micro/RawRecords.vue';
import SkeletonComponent from '@/components/micro/SkeletonComponent.vue';
import { QueryResult, emptyDnsServer, emptyResponse } from '@/model/Api';
import { ApiErrorType, toErrorType } from '@/model/ApiErrorAlert';
import AdUnit from '@/components/ads/AdUnit.vue';
import { domainNameToUrlPath } from '@/common/domainName';
import { ReverseIpResults } from '@/common/adSlots';

const props = defineProps({
  routerIp: {
    type: String,
    required: true,
  },
});

const router = useRouter();

const ip = ref('');
const domainPunycode = ref('');
const loading = ref(true);
const error = ref<ApiErrorType | null>(null);
const showSidebar = ref(window.innerWidth > 991);
const cancelToken = ref<Canceler | null>(null);
const queryResult = ref<QueryResult>({
  query: {
    server: emptyDnsServer,
    domain: ip.value,
    recordType: 'PTR',
  },
  response: emptyResponse,
});

function redirectTo(newIp: string): void {
  router.push(`/ip/${domainNameToUrlPath(newIp)}/domain-names/`);
}

function load() {
  loading.value = true;
  error.value = null;

  if (cancelToken.value) {
    cancelToken.value();
  }
  const axiosSource = axios.CancelToken.source();
  cancelToken.value = axiosSource.cancel;

  axios
    .post<QueryResult>('api/v1/ptr-records', {
      domain: ip.value,
      dnsServer: 'authoritative',
    }, {
      cancelToken: axiosSource.token,
    })
    .then((response) => {
      queryResult.value = response.data;
      loading.value = false;
    })
    .catch((e) => {
      if (!axios.isCancel(e)) {
        error.value = toErrorType(e.response.data.error);
      }
    });
}

watch(() => props.routerIp, () => {
  if (domainPunycode.value !== props.routerIp) {
    ip.value = decodeURI(props.routerIp)
      .toLowerCase();
    domainPunycode.value = decodeURI(props.routerIp)
      .toLowerCase();
    load();
  }
}, {
  immediate: true,
});

window.addEventListener('resize', () => {
  showSidebar.value = window.innerWidth > 991;
});
</script>
